/**
 * Auto bet as betting ZONES (B32). The player names a band of prices, and every
 * round the rule puts the same stake on each tile the round prices inside it —
 * a middle-zone player is not choosing "two in" or "hole miss hole", they are
 * choosing "the 3x to 10x shots", and which tiles those are is the round's
 * answer, not theirs.
 *
 * Pure: the rule is planned against `tiles` — the priced-AND-bettable set
 * `applyRoundOdds` already hands the board — and against the same `ChipLimits`
 * a finger is checked against, so nothing this module admits can be refused
 * by the reducer for a reason it could have seen. The plan is then placed
 * through the ordinary `place` actions and rides the ordinary close-time
 * submission; auto bet changes WHO presses the button and nothing else.
 *
 * Every refusal is a SKIP of the whole round, never a quieter bet: a stake the
 * ceiling would clip, a total the round ceiling would cut, a balance that
 * covers only some of the placements — each of these skips the round and says
 * why. A player who set 2 per option must never find 0.7 on a tile.
 *
 * Money is whatever the chips are in (wire units, converted by the caller),
 * and multipliers are the UNROUNDED numbers the tiles price from: "10x" on the
 * board means the number the tile shows, so a bound compares against the
 * price, not its floored text.
 */

import type { ChipLimits } from './birdieChips'
import { ROUND_KEY_RE, type BirdieTileView, type TileKind } from './tileViewModel'

export type ZonePreset = 'short' | 'middle' | 'long' | 'far' | 'custom'

/** Inclusive at both ends; `max: null` is "and up". */
export interface ZoneBounds {
  min: number
  max: number | null
}

/**
 * The four named zones, edge to edge from the bottom of the axis. A shared
 * edge belongs to both neighbours (3x is short AND middle), which is the
 * inclusive reading a player expects of "3x to 10x".
 */
export const ZONE_PRESETS: Readonly<Record<Exclude<ZonePreset, 'custom'>, ZoneBounds>> = {
  short: { min: 1.5, max: 3 },
  middle: { min: 3, max: 10 },
  long: { min: 10, max: 40 },
  far: { min: 40, max: null }
}

const PRESET_ORDER: readonly Exclude<ZonePreset, 'custom'>[] = ['short', 'middle', 'long', 'far']

export type ZoneMarkets = 'exact' | 'count' | 'both'

export interface AutoBetZone extends ZoneBounds {
  preset: ZonePreset
}

/**
 * The round-type pair is its own plain "every round" repeat, deliberately not
 * part of a zone: FROST and FIRE are priced from the seed at a fixed chance,
 * so any middle zone would catch them every single round — and a repeat is
 * what it is, so it gets its own toggle. It rides the rule's ONE stake: a
 * second stake beside it was a whole row of the sheet spent on a number the
 * player had already chosen once (owner 2026-09-17).
 */
export interface RoundTypeRepeat {
  frost: boolean
  fire: boolean
}

export interface AutoBetRule {
  zone: AutoBetZone
  markets: ZoneMarkets
  /** Per caught option, and per round-type repeat. */
  stake: number
  /**
   * The most the rule may put on one round, the round-type pair included.
   * Money, so the planner compares like with like — but the sheet keeps it at
   * `stake × a count of options`, and `Infinity` is "no limit".
   */
  roundCeiling: number
  /** `null` = until stopped. */
  roundsLeft: number | null
  roundType: RoundTypeRepeat
}

export type AutoBetSkip = 'unpriced' | 'nothing-in-zone' | 'clamped' | 'over-ceiling' | 'balance'

export interface AutoBetPlacement {
  optionId: string
  marketId: string
  amount: number
  key: string
  multiplier: number
}

/**
 * A skipped plan still carries the placements it would have made, so the
 * sheet can show the player what the rule wanted and why it stood down. The
 * one exception is `unpriced`: with no odds there is nothing to have wanted.
 */
export interface AutoBetPlan {
  placements: readonly AutoBetPlacement[]
  total: number
  skip?: AutoBetSkip
  /**
   * How many options the zone CAUGHT, when the ceiling could not pay for all
   * of them and the plan was trimmed to what fits. Absent when the whole catch
   * was placed. The board says it out loud — a round that quietly placed four
   * of nine would be the feature lying about what it did.
   */
  cappedFrom?: number
}

/**
 * `roundCeiling` is `stake × DEFAULT_MAX_OPTIONS` and must stay that way — the
 * sheet reads the ceiling back as a count on every render, so a default that
 * was not a whole number of stakes would open on a rung the rule is not on.
 * The constant is declared further down (it belongs beside its ladder), hence
 * the literal here, which the default-rule test pins to it.
 */
export const DEFAULT_RULE: AutoBetRule = {
  zone: { preset: 'middle', ...ZONE_PRESETS.middle },
  markets: 'both',
  stake: 1,
  roundCeiling: 6,
  roundsLeft: 10,
  roundType: { frost: false, fire: false }
}

export function zoneCatches(zone: ZoneBounds, multiplier: number): boolean {
  return multiplier >= zone.min && (zone.max === null || multiplier <= zone.max)
}

const admits = (markets: ZoneMarkets, kind: TileKind): boolean =>
  kind !== 'round' && (markets === 'both' || markets === kind)

/**
 * What the rule puts on a tile, or undefined for a tile it does not want. The
 * round-type pair is answered by its toggle alone; every other tile by the
 * markets filter and the zone. Both get the rule's one stake. A stake that is
 * not a positive number places nothing, the same guard the board's own tap has.
 */
function stakeFor(tile: BirdieTileView, rule: AutoBetRule): number | undefined {
  let wanted: boolean
  if (tile.kind === 'round') {
    wanted =
      (tile.key === 'FROST' && rule.roundType.frost) || (tile.key === 'FIRE' && rule.roundType.fire)
  } else {
    // `roundMultiplier`, not `multiplier`: the band is a statement about THIS
    // ROUND's prices. An inventory booster the player claimed by hand raises
    // what one tile shows, and it must not thereby pull that tile into — or push
    // it out of — an armed rule's zone (B38).
    wanted = admits(rule.markets, tile.kind) && zoneCatches(rule.zone, tile.roundMultiplier)
  }
  return wanted && rule.stake > 0 ? rule.stake : undefined
}

/**
 * Both sides of this comparison are sums and products of the same chip value —
 * ten chips of 0.0001 add up to a hair more than 0.0001 × 10 — so a plan that
 * lands exactly on its own ceiling must not be floated over it. A relative
 * epsilon, because the money can be a yen or a satoshi.
 */
const overCeiling = (total: number, ceiling: number): boolean =>
  total - ceiling > Math.max(total, ceiling) * 1e-9

/**
 * What fits under the ceiling, and in which order the ceiling is spent.
 *
 * The round-type repeats go first: the player named Frost and Fire and asked
 * for them EVERY round, so they are not candidates for being dropped by a
 * ceiling the zone blew. Everything the zone caught then fills the rest
 * SHORTEST PRICE FIRST (owner 2026-09-17) — deterministic, so two identical
 * rounds place identically, and it buys the most options the ceiling can hold.
 */
function fitToCeiling(
  placements: readonly AutoBetPlacement[],
  ceiling: number
): { kept: AutoBetPlacement[]; total: number } {
  const isRoundType = (p: AutoBetPlacement): boolean => ROUND_KEY_RE.test(p.key)
  const order = [
    ...placements.filter(isRoundType),
    ...placements.filter((p) => !isRoundType(p)).sort((a, b) => a.multiplier - b.multiplier)
  ]
  const kept: AutoBetPlacement[] = []
  let total = 0
  for (const placement of order) {
    if (overCeiling(total + placement.amount, ceiling)) continue
    kept.push(placement)
    total += placement.amount
  }
  // Back into the tiles' own order, so the board places them the way it reads.
  const keptIds = new Set(kept.map((p) => p.optionId))
  return { kept: placements.filter((p) => keptIds.has(p.optionId)), total }
}

/**
 * The round's plan, in the tiles' order. The guards run in the order the
 * reducer would hit them, so the skip named is the first thing that would
 * have stopped a finger placing the same chips: no odds, nothing wanted, a
 * stake over an option's ceiling, a total over the rule's own ceiling, and only
 * then the balance — an unloaded balance counts as short, as it does for a tap.
 */
export function planAutoBets(input: {
  tiles: readonly BirdieTileView[]
  rule: AutoBetRule
  limits: ChipLimits
  marketIdByOption: ReadonlyMap<string, string>
}): AutoBetPlan {
  const { tiles, rule, limits, marketIdByOption } = input
  if (!limits.roundPriced) return { placements: [], total: 0, skip: 'unpriced' }

  const placements: AutoBetPlacement[] = []
  let total = 0
  let clamped = false
  for (const tile of tiles) {
    if (!limits.bettableIds.has(tile.optionId)) continue
    const amount = stakeFor(tile, rule)
    if (amount === undefined) continue
    const marketId = marketIdByOption.get(tile.optionId)
    if (marketId === undefined) continue
    placements.push({
      optionId: tile.optionId,
      marketId,
      amount,
      key: tile.key,
      multiplier: tile.roundMultiplier
    })
    total += amount
    if (amount > (limits.optionMax.get(tile.optionId) ?? Number.POSITIVE_INFINITY)) clamped = true
  }

  if (placements.length === 0) return { placements, total: 0, skip: 'nothing-in-zone' }
  if (clamped) return { placements, total, skip: 'clamped' }
  if (overCeiling(total, rule.roundCeiling)) {
    // Trim rather than stand down. Skipping the whole round was the old
    // behaviour and it threw away most rounds on a wide zone with a small
    // ceiling (owner 2026-09-17). A ceiling that cannot pay for even the
    // cheapest single option is still a real skip — there is nothing to trim to.
    const { kept, total: keptTotal } = fitToCeiling(placements, rule.roundCeiling)
    if (kept.length === 0) return { placements, total, skip: 'over-ceiling' }
    if (!limits.balanceLoaded || keptTotal > limits.balance) {
      return { placements: kept, total: keptTotal, skip: 'balance' }
    }
    return { placements: kept, total: keptTotal, cappedFrom: placements.length }
  }
  if (!limits.balanceLoaded || total > limits.balance) return { placements, total, skip: 'balance' }
  return { placements, total }
}

/**
 * The tiles an armed rule passes over — the board's ghost set. Read off the
 * plan's placements rather than off the zone again, so the ghost and the chip
 * can never disagree about a tile; and read off the plan whatever its skip,
 * because a round the ceiling refused still had a zone, and the zone is what
 * the ghost shows. An unpriced round plans nothing and ghosts nothing.
 */
export function zoneMissIds(input: {
  tiles: readonly BirdieTileView[]
  rule: AutoBetRule
  limits: ChipLimits
  marketIdByOption: ReadonlyMap<string, string>
}): ReadonlySet<string> {
  // Planned against an UNREACHABLE ceiling on purpose: the ghost says what the
  // ZONE will and will not take, and a ceiling that trims a round does not
  // change what the zone wanted. Ghosting the trimmed set instead would mark
  // options the rule is actively hunting as ones it passes over, and the mark
  // would move every time the stake changed.
  const plan = planAutoBets({
    ...input,
    rule: { ...input.rule, roundCeiling: Number.POSITIVE_INFINITY }
  })
  if (plan.skip === 'unpriced') return new Set()
  const caught = new Set<string>()
  for (const placement of plan.placements) caught.add(placement.optionId)
  const missed = new Set<string>()
  for (const tile of input.tiles) if (!caught.has(tile.optionId)) missed.add(tile.optionId)
  return missed
}

/**
 * The round ceiling as the player sets it: how many OPTIONS a round may take.
 * A count is currency-proof where a money ladder was not — the old one was six
 * multiples of the lowest chip, which read as dollars on a dollar and as
 * nothing recognisable on a yen — and it is the question the player is really
 * answering, since every option takes the same stake.
 *
 * The bottom rung is TWO. One was considered and refused (owner 2026-09-17):
 * the ceiling caps the round's money, it does not pick a bet, so a one-option
 * ceiling would stand the rule down on nearly every round the zone caught more
 * than one tile — and the only way to make it place a single bet would be for
 * auto bet to choose WHICH option, which is the one thing it must never do.
 */
// The board sells twelve options, so "no limit" was always twelve wearing an
// infinity sign (owner 2026-09-17). The top rung says the true number and the
// ceiling is finite at every rung, which takes the Infinity sentinel — and its
// formatting special cases — out of the rule entirely.
//
// EVERY WHOLE NUMBER from two to twelve (owner 2026-09-18). The ladder was
// sparse — 2, 3, 5, 7, 10, 12 — from the days when the rung list doubled as the
// ceiling's granularity in money; it is a count of options now, and a ± control
// that jumps 3 → 5 → 7 is a control that cannot be asked for four. Eleven rungs
// walked one at a time is what a stepper is for.
export const MAX_OPTIONS: readonly number[] = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

/**
 * Where a fresh rule starts, and what `DEFAULT_RULE`'s ceiling is a stake times
 * (owner 2026-09-18: six, not ten). Half the board rather than five sixths of
 * it: the ceiling is the safety rail, so the rule a player has not touched yet
 * is the cautious one.
 */
export const DEFAULT_MAX_OPTIONS = 6

/** The counts the Stop-after stepper offers, `null` above the top being "until I stop". */
export const STOP_AFTER_ROUNDS: readonly number[] = [5, 10, 25, 50]

/** The money a count of options works out to. Always finite — see MAX_OPTIONS. */
export function ceilingForOptions(stake: number, options: number): number {
  return stake * options
}

/**
 * The count back off the money, so the stepper and the rule can never
 * disagree. A ceiling that is not a whole number of stakes — a rule armed
 * before the ladder moved under it — reads as the nearest rung, ties low.
 */
export function optionsForCeiling(stake: number, ceiling: number): number {
  // A rule armed before the ladder lost its no-limit rung carries an infinite
  // ceiling. It reads as the top rung, which is what it always meant.
  if (!Number.isFinite(ceiling)) return MAX_OPTIONS[MAX_OPTIONS.length - 1] ?? DEFAULT_MAX_OPTIONS
  if (!(stake > 0) || !Number.isFinite(stake)) return DEFAULT_MAX_OPTIONS
  const wanted = ceiling / stake
  let best = DEFAULT_MAX_OPTIONS
  let bestGap = Number.POSITIVE_INFINITY
  for (const options of MAX_OPTIONS) {
    const gap = Math.abs(options - wanted)
    if (gap < bestGap) {
      best = options
      bestGap = gap
    }
  }
  return best
}

/**
 * One rung along a ladder whose top rung is `null` — no limit, until I stop.
 * Neither end wraps: a stepper that jumped from the top back to the bottom
 * would arm a rule the player did not mean. A value BETWEEN rungs (an armed
 * rule counting down is the only way to get one) steps to its neighbours
 * rather than snapping first, so + and − both move it the way it reads.
 */
function stepLadder(
  ladder: readonly number[],
  current: number | null,
  direction: 1 | -1
): number | null {
  if (current === null) return direction === 1 ? null : (ladder[ladder.length - 1] ?? null)
  if (direction === 1) return ladder.find((rung) => rung > current) ?? null
  let lower = current
  for (const rung of ladder) if (rung < current) lower = rung
  return lower
}

export const stepRounds = (current: number | null, direction: 1 | -1): number | null =>
  stepLadder(STOP_AFTER_ROUNDS, current, direction)

/**
 * One rung along the options ladder. Unlike the rounds ladder this one has no
 * open end — twelve IS every option — so the top clamps rather than stepping
 * into "no limit".
 */
export const stepOptions = (current: number, direction: 1 | -1): number => {
  const top = MAX_OPTIONS[MAX_OPTIONS.length - 1] ?? current
  const stepped = stepLadder(MAX_OPTIONS, current, direction)
  return stepped ?? top
}

/**
 * The board's ghost set as the board should draw it. The ghost is a BETTING-
 * WINDOW affordance — it says what the armed zone will and will not take on a
 * round the player can still change — so it ends the moment the board stops
 * taking chips. It has to: `.birdie-tile--zone-miss` carries two classes to
 * outrank `--placed`, which also puts it over `--winner`, `--hot`, `--dead`
 * and `--alive-mine`, and a ghost left on the locked board would dim the
 * settled bloom and the third-putt ember it is drawn across.
 */
export function ghostIdsFor(input: {
  tiles: readonly BirdieTileView[]
  rule: AutoBetRule | null
  limits: ChipLimits
  marketIdByOption: ReadonlyMap<string, string>
  /** The window a finger could place in — the rule's own. */
  canPlace: boolean
}): ReadonlySet<string> {
  const { rule, canPlace } = input
  if (rule === null || !canPlace) return new Set()
  return zoneMissIds({ ...input, rule })
}

/**
 * Whether the board's own way INTO the sheet belongs on screen (owner
 * 2026-09-17). Entry is not placing: the machine is happy to be armed on a
 * round it cannot bet on — it only plans an open, priced round — so a player
 * who sat out the current one can set the rule up while it runs and have it
 * ready for the next.
 *
 * `tilesLocked` is the whole gate on the strip's side: the strip is on screen
 * exactly while it is false, and it carries an auto button of its own, so
 * anything but this would put two ways in on screen at once.
 *
 * The rest is "nothing is riding on this round" — no chips waiting to go out,
 * no submission of this player's, no bets already accepted. A round the player
 * is IN is a round whose slot belongs to what happens to those bets, not to a
 * settings door.
 */
export function autoBetEntryVisible(input: {
  armed: boolean
  tilesLocked: boolean
  /** All three kept for the call site's shape; this round no longer gates the door. */
  betsSubmitted?: boolean
  chipCount?: number
  betsThisRound?: number
}): boolean {
  const { armed, tilesLocked } = input
  // NOTHING about this round hides the door (owner 2026-09-18: "if i turn off
  // auto bet during a round … it should still be possible after that to create
  // a new auto bet", and the same for a round the player simply bet on by
  // hand). The earlier rules — held chips, a submission, an accepted bet —
  // were all guarding a conflict that does not exist: arming is INERT until a
  // round opens, because the machine plans only an open, priced round. Bets
  // already on the wire are settled by the round they were placed in whatever
  // the player sets up meanwhile.
  //
  // So the whole gate is: the rule is off, and the strip that carries the
  // other way in is off screen. `tilesLocked` is exactly when that is true, so
  // the two doors can never be open at once.
  return !armed && tilesLocked
}

/**
 * A rule whose stake is a rung of the strip's own ladder. The default rule
 * says 1, which is a rung on a dollar ladder and nothing at all on a yen one —
 * below the min bet, so a plan built on it would go to the wire and be refused
 * there. The lowest rung is the honest default: it is the min bet, which is
 * where every other game's first chip sits.
 *
 * The ceiling follows the stake by its OPTION COUNT, not by its money: the
 * defect this fixes was a ceiling left behind on a snapped stake, which put
 * every round over a ceiling smaller than one option. A rule already on the
 * ladder comes back as the same object, so the sheet's draft is not churned
 * on every open.
 */
export function ruleOnLadder(rule: AutoBetRule, chipValues: readonly number[]): AutoBetRule {
  const lowest = chipValues[0]
  if (lowest === undefined) return rule
  const stake = chipValues.includes(rule.stake) ? rule.stake : lowest
  const roundCeiling = ceilingForOptions(stake, optionsForCeiling(rule.stake, rule.roundCeiling))
  if (stake === rule.stake && roundCeiling === rule.roundCeiling) return rule
  return { ...rule, stake, roundCeiling }
}

/**
 * The Custom slider's track: 1.5x to 200x, log-scaled so the short zone gets
 * the same room as the far one (on a linear track everything under 10x would
 * be a sliver at the left edge). The ticks are the mockup's.
 */
export const AXIS_MIN = 1.5
export const AXIS_MAX = 200
export const AXIS_TICKS: readonly number[] = [2, 5, 10, 25, 50, 100]

const LOG_MIN = Math.log(AXIS_MIN)
const LOG_SPAN = Math.log(AXIS_MAX) - LOG_MIN

/** 0..1 along the track, clamped; a price that is not a number sits at 0. */
export function axisPos(multiplier: number): number {
  if (!(multiplier > AXIS_MIN)) return 0
  if (multiplier >= AXIS_MAX) return 1
  return (Math.log(multiplier) - LOG_MIN) / LOG_SPAN
}

/** The inverse: a track position back to a price, clamped to the ends. */
export function axisValue(pos: number): number {
  if (!(pos > 0)) return AXIS_MIN
  if (pos >= 1) return AXIS_MAX
  return Math.exp(LOG_MIN + pos * LOG_SPAN)
}

/**
 * The two shapes a zone reads as, with the numbers already formatted by the
 * caller. The copy itself lives with the rest of the board's strings — this
 * only says which sentence applies and hands over its parts.
 */
export function zoneLabel(
  zone: ZoneBounds,
  fmt: (m: number) => string
): { kind: 'between'; min: string; max: string } | { kind: 'andUp'; min: string } {
  if (zone.max === null) return { kind: 'andUp', min: fmt(zone.min) }
  return { kind: 'between', min: fmt(zone.min), max: fmt(zone.max) }
}

/**
 * The preset whose bounds these are exactly, else `custom`. Judged by the
 * bounds alone so the preset chips light from the slider's position, not from
 * a tag that could disagree with it.
 */
export function presetFor(bounds: ZoneBounds): ZonePreset {
  for (const name of PRESET_ORDER) {
    const preset = ZONE_PRESETS[name]
    if (preset.min === bounds.min && preset.max === bounds.max) return name
  }
  return 'custom'
}
